import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Users, Code2, Cpu, Database, Network, Workflow,
  CheckCircle2, ArrowRight, Layers, Cog, BarChart3, Shield,
  Search, Zap, GitBranch,
} from 'lucide-react';
import PageHero from '../components/shared/PageHero';
import CTABanner from '../components/shared/CTABanner';
import TechPattern from '../components/shared/TechPattern';

const productTabs = [
  {
    id: 'engineering',
    label: 'AI Engineering',
    icon: Code2,
    color: '#2563eb',
    gradient: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
    hero: {
      title: 'AI Engineering',
      subtitle: 'Software engineers powered by AI. Custom-built solutions engineered to fit your exact business processes and technical infrastructure.',
    },
    features: [
      { icon: Cpu, title: 'AI-Powered Development', desc: 'Software engineers leveraging AI to build, test, and deploy faster with higher quality.' },
      { icon: Network, title: 'System Integration', desc: 'Seamless integration with your existing tools, APIs, and workflows. Zero disruption.' },
      { icon: Database, title: 'Data Pipeline Architecture', desc: 'Robust data pipelines that feed your systems with clean, structured data in real-time.' },
      { icon: Workflow, title: 'MLOps & Infrastructure', desc: 'Production-grade infrastructure with monitoring, scaling, and continuous deployment.' },
      { icon: Shield, title: 'Enterprise Security', desc: 'SOC2-compliant architecture with encrypted data at rest and in transit.' },
      { icon: BarChart3, title: 'Performance Analytics', desc: 'Comprehensive dashboards tracking accuracy, latency, and business impact.' },
    ],
    useCases: [
      'Intelligent document processing and extraction',
      'Predictive analytics and forecasting',
      'Natural language understanding for internal tools',
      'Computer vision for quality control',
      'Custom API and microservice development',
    ],
  },
  {
    id: 'teams',
    label: 'AI Teams',
    icon: Layers,
    color: '#7c3aed',
    gradient: 'linear-gradient(135deg, #7c3aed 0%, #ec4899 100%)',
    hero: {
      title: 'AI Teams',
      subtitle: 'Software engineers working alongside AI agents. The complete solution for companies that want a full AI-powered workforce.',
    },
    features: [
      { icon: Layers, title: 'End-to-End Operations', desc: 'From architecture to autonomous execution. Engineers and AI agents working as one team.' },
      { icon: Users, title: 'Custom Team Composition', desc: 'Engineers and agent roles tailored to your specific business reality.' },
      { icon: Search, title: 'Diagnostic Onboarding', desc: 'In minutes, the system analyzes your environment and learns your processes.' },
      { icon: GitBranch, title: 'Workflow Integration', desc: 'Connects to your existing tools and processes. No migration needed.' },
      { icon: Cog, title: 'Continuous Evolution', desc: 'The team evolves with your business. New capabilities, automatic improvements.' },
      { icon: Zap, title: 'Scale on Demand', desc: 'Scale across departments without hiring. New teams deployed in minutes.' },
    ],
    useCases: [
      'Full engineering operations with AI augmentation',
      'Multi-department AI workforce deployment',
      'End-to-end product development support',
      'Enterprise-wide process optimization',
      'Complete digital transformation programs',
    ],
  },
];

const ProductsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const product = productTabs[activeTab];
  const TabIcon = product.icon;

  return (
    <>
      <PageHero
        label="Our Products"
        labelIcon={<Layers size={14} />}
        title={<>AI Solutions That <span className="gradient-text">Work For You</span></>}
        subtitle="From AI-powered software engineering to complete AI teams, we offer solutions to transform your operations."
      />

      <section className="section" style={{ paddingTop: 0, position: 'relative', overflow: 'hidden' }}>
        <TechPattern variant="nodes" opacity={0.03} />
        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', marginBottom: 48, flexWrap: 'wrap' }}>
            {productTabs.map((tab, i) => {
              const Icon = tab.icon;
              return (
                <button key={tab.id} onClick={() => setActiveTab(i)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '14px 28px',
                    borderRadius: 100, fontSize: '0.95rem', fontWeight: 600,
                    background: activeTab === i ? `${tab.color}08` : 'white',
                    border: `1px solid ${activeTab === i ? `${tab.color}30` : 'var(--border-color)'}`,
                    color: activeTab === i ? tab.color : 'var(--text-secondary)',
                    cursor: 'pointer', transition: 'all 0.3s', boxShadow: 'var(--shadow-card)',
                  }}
                >
                  <Icon size={18} />{tab.label}
                </button>
              );
            })}
          </div>

          <motion.div key={activeTab} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
            <div style={{
              background: `${product.color}06`, borderRadius: 'var(--radius-lg)', padding: '48px 40px',
              border: `1px solid ${product.color}15`, marginBottom: 48, textAlign: 'center',
              position: 'relative', overflow: 'hidden', boxShadow: 'var(--shadow-card)',
            }}>
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: product.gradient }} />
              <div style={{
                width: 72, height: 72, borderRadius: 20, background: product.gradient,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 20px', boxShadow: `0 8px 32px ${product.color}20`,
              }}>
                <TabIcon size={36} color="white" />
              </div>
              <h2 style={{ fontSize: 'clamp(1.8rem, 3vw, 2.5rem)', fontWeight: 800, marginBottom: 12 }}>{product.hero.title}</h2>
              <p style={{ fontSize: '1.1rem', color: 'var(--text-secondary)', maxWidth: 600, margin: '0 auto', lineHeight: 1.7 }}>{product.hero.subtitle}</p>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20, marginBottom: 48 }}>
              {product.features.map((feature, i) => {
                const FeatureIcon = feature.icon;
                return (
                  <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: i * 0.06 }} whileHover={{ y: -6 }}
                    style={{
                      background: 'white', borderRadius: 'var(--radius)', padding: '28px 22px',
                      border: '1px solid var(--border-color)', position: 'relative', overflow: 'hidden', boxShadow: 'var(--shadow-card)',
                    }}
                  >
                    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: product.color, opacity: 0.5 }} />
                    <div style={{
                      width: 44, height: 44, borderRadius: 12, background: `${product.color}0a`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16,
                    }}>
                      <FeatureIcon size={22} color={product.color} />
                    </div>
                    <h4 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: 8 }}>{feature.title}</h4>
                    <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', lineHeight: 1.6 }}>{feature.desc}</p>
                  </motion.div>
                );
              })}
            </div>

            <div style={{
              background: 'white', borderRadius: 'var(--radius-lg)', padding: '36px 32px',
              border: '1px solid var(--border-color)', marginBottom: 48, boxShadow: 'var(--shadow-card)',
            }}>
              <h3 style={{ fontSize: '1.2rem', fontWeight: 700, marginBottom: 24 }}>Common Use Cases</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>
                {product.useCases.map((uc, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <CheckCircle2 size={16} color={product.color} style={{ flexShrink: 0 }} />
                    <span style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>{uc}</span>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ textAlign: 'center' }}>
              <Link to="/contact" style={{
                display: 'inline-flex', alignItems: 'center', gap: 8, padding: '16px 36px',
                background: product.gradient, borderRadius: 100, fontSize: '1.05rem', fontWeight: 600,
                color: 'white', boxShadow: `0 4px 24px ${product.color}20`, transition: 'transform 0.2s',
              }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
              >
                Get Started with {product.hero.title} <ArrowRight size={18} />
              </Link>
            </div>
          </motion.div>
        </div>

        <style>{`
          @media (max-width: 768px) {
            section > .container div[style*="grid-template-columns: repeat(3"] { grid-template-columns: 1fr !important; }
          }
        `}</style>
      </section>

      <CTABanner />
    </>
  );
};

export default ProductsPage;
